import { FeatureSwitch } from '../../_models/admin/feature-switch.models';

/** One New Features tab: a category and the switches listed under it. */
export interface FeatureSwitchTab {
  /** Lower-case key, as used in the ?tab= query parameter. */
  key: string;
  label: string;
  switches: FeatureSwitch[];
  /** The switches as listed: each sub-switch straight after its parent, indented by depth. */
  rows: FeatureSwitchRow[];
}

/** One listed switch, with where it sits under its parent and whether a parent holds it off. */
export interface FeatureSwitchRow {
  item: FeatureSwitch;
  /** 0 for a top-level switch, 1 for its sub-switches, and so on. */
  depth: number;
  /**
   * The display name of the nearest switch up the chain that is off (or the key of a parent with no row),
   * or null when every parent is on. A sub-switch is inactive while this is set, whatever its own toggle says.
   */
  blockedBy: string | null;
}

/** Matches the API's MaxParentDepth: a chain deeper than this, or a cycle, is not followed. */
const MAX_PARENT_DEPTH = 8;

function parentOf(item: FeatureSwitch): string | null {
  const key = (item && item.parentKey || '').trim();
  return key ? key : null;
}

/** The nearest ancestor that holds this switch off, as its display name; null when the chain is on. */
export function blockedBy(item: FeatureSwitch, all: FeatureSwitch[]): string | null {
  const byKey = new Map((all || []).map(s => [s.switchKey.toLowerCase(), s] as [string, FeatureSwitch]));
  const seen = new Set<string>([item.switchKey.toLowerCase()]);
  let parentKey = parentOf(item);
  for (let depth = 0; parentKey && depth < MAX_PARENT_DEPTH; depth++) {
    const parent = byKey.get(parentKey.toLowerCase());
    if (!parent || seen.has(parent.switchKey.toLowerCase())) {
      return parent ? parent.displayName : parentKey;
    }
    if (!parent.isEnabled) {
      return parent.displayName;
    }
    seen.add(parent.switchKey.toLowerCase());
    parentKey = parentOf(parent);
  }
  return parentKey ? parentKey : null;
}

/**
 * Orders a tab's switches for display: top-level switches in their given order, each followed by its
 * sub-switches (depth first). A sub-switch whose parent is not in the list is shown at the top level.
 * `all` is every switch, so a parent held off from another tab still counts.
 */
export function nestRows(switches: FeatureSwitch[], all: FeatureSwitch[] = switches): FeatureSwitchRow[] {
  const list = switches || [];
  const inList = new Set(list.map(s => s.switchKey.toLowerCase()));
  const children = new Map<string, FeatureSwitch[]>();
  list.forEach(s => {
    const parent = parentOf(s);
    if (parent && inList.has(parent.toLowerCase())) {
      const k = parent.toLowerCase();
      children.set(k, [...(children.get(k) || []), s]);
    }
  });

  const rows: FeatureSwitchRow[] = [];
  const placed = new Set<string>();
  const place = (item: FeatureSwitch, depth: number) => {
    const key = item.switchKey.toLowerCase();
    if (placed.has(key)) {
      return;
    }
    placed.add(key);
    rows.push({ item, depth, blockedBy: blockedBy(item, all) });
    (children.get(key) || []).forEach(child => place(child, Math.min(depth + 1, MAX_PARENT_DEPTH)));
  };

  list.filter(s => !parentOf(s) || !inList.has(parentOf(s).toLowerCase())).forEach(s => place(s, 0));
  // Anything left sits in a cycle: list it rather than hide it.
  list.forEach(s => place(s, 0));
  return rows;
}

/** Every category, in tab order. A switch with a missing or unknown category is listed under Other. */
export const FEATURE_SWITCH_CATEGORIES: ReadonlyArray<string> = ['Search', 'Sync', 'Access', 'Admin', 'Other'];

/** Shown even with no switches, because it holds other editors: the staff sync panel and the role mapping. */
const ALWAYS_SHOWN: ReadonlyArray<string> = ['Sync'];

function categoryOf(item: FeatureSwitch): string {
  const raw = (item && item.category || '').trim().toLowerCase();
  return FEATURE_SWITCH_CATEGORIES.find(c => c.toLowerCase() === raw) || 'Other';
}

/** The tabs to show, in category order: each category that has switches, plus Sync always. */
export function buildTabs(switches: FeatureSwitch[]): FeatureSwitchTab[] {
  return FEATURE_SWITCH_CATEGORIES
    .map(label => {
      const inTab = (switches || []).filter(s => categoryOf(s) === label);
      return { key: label.toLowerCase(), label, switches: inTab, rows: nestRows(inTab, switches || []) };
    })
    .filter(tab => tab.switches.length > 0 || ALWAYS_SHOWN.includes(tab.label));
}

/**
 * The tab to open: the one named by ?tab= when it is shown, otherwise the first tab that has switches,
 * otherwise the first tab.
 */
export function initialTabIndex(tabs: FeatureSwitchTab[], requested: string | null): number {
  const wanted = (requested || '').trim().toLowerCase();
  const named = tabs.findIndex(t => t.key === wanted);
  if (named >= 0) {
    return named;
  }
  const withSwitches = tabs.findIndex(t => t.switches.length > 0);
  return withSwitches >= 0 ? withSwitches : 0;
}
